import {
  put, takeLatest,
} from 'redux-saga/effects';
import { select } from '~/_shared/utils/saga/effects';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { MainMenuItems } from '~/mainMenu/mainMenuItem.enum';
import { MapTemplates } from '~/map/mapTemplatesModal/mapTemplates.helpers';
import { SidebarApp } from '~/sidebar/sidebarApp.enum';
import { BOUNDARY_GROUP_CREATE_REQUEST } from '~/store/boundaryGroups/boundaryGroups.actionTypes';
import { BOUNDARY_TERRITORY_GROUP_CREATE_REQUEST } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionTypes';
import {
  clearActiveMapTemplate, openLeftSidebarApp,
} from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import { frontendStateLeftSidebarActiveMapTemplateSelector } from '~/store/frontendState/leftSidebar/leftSidebar.selectors';
import { BOUNDARY_DRAW_EDIT_OPEN_MODAL } from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.actionTypes';
import { BOUNDARY_SELECT_EDIT_CUSTOM_BOUNDARY_OPEN_MODAL } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionTypes';
import { FRONTEND_STATE_DIRECTIONS_SET_WAYPOINTS } from '~/store/frontendState/mapTools/directions/directions.actionTypes';
import { FRONTEND_STATE_SEARCH_SHOW } from '~/store/frontendState/mapTools/search/search.actionTypes';
import { MOVE_MARKERS_OPEN_MODAL } from '~/store/frontendState/moveMarkers/moveMarkers.actionTypes';
import { placesToolSetStartingLocation } from '~/store/frontendState/places/places.actionCreators';
import { PROXIMITY_TOOL_SET_STARTING_LOCATION } from '~/store/frontendState/proximityTool/proximityTool.actionTypes';
import { MAP_SETTINGS_GROUPING_SET_ACTIVE_GROUP_COLUMNS } from '~/store/mapSettings/grouping/mapSettingsGrouping.actionTypes';
import { MAP_SETTINGS_HEATMAP_ADD_HEATMAP } from '~/store/mapSettings/heatmaps/mapSettingsHeatmaps.actionTypes';
import { openNewModal } from '~/store/modal/modal.actionCreators';

// Actions where the user starts using a tool for real (not just template pre-fill) — most aren't
// MAP_SETTINGS actions, so clearing on settings-sync wouldn't catch them. Add new template-relevant actions here.
// action creators can be passed directly (saga extracts .type)
const ACTIVE_MAP_TEMPLATE_CLEAR_TRIGGERS = [
  BOUNDARY_DRAW_EDIT_OPEN_MODAL,
  BOUNDARY_GROUP_CREATE_REQUEST,
  BOUNDARY_SELECT_EDIT_CUSTOM_BOUNDARY_OPEN_MODAL,
  BOUNDARY_TERRITORY_GROUP_CREATE_REQUEST,
  FRONTEND_STATE_DIRECTIONS_SET_WAYPOINTS,
  FRONTEND_STATE_SEARCH_SHOW,
  MAP_SETTINGS_GROUPING_SET_ACTIVE_GROUP_COLUMNS,
  MAP_SETTINGS_HEATMAP_ADD_HEATMAP,
  MOVE_MARKERS_OPEN_MODAL,
  PROXIMITY_TOOL_SET_STARTING_LOCATION,
  placesToolSetStartingLocation,
];

export function* leftSidebarSagas() {
  yield takeLatest(openLeftSidebarApp.type, openToolboxlessLeftMenuTool);
  yield takeLatest(openLeftSidebarApp.type, clearTemplateOnAppChange);
  yield takeLatest(ACTIVE_MAP_TEMPLATE_CLEAR_TRIGGERS, clearActiveMapTemplateOnToolAction);
}

function* openToolboxlessLeftMenuTool(
  action: ReturnType<typeof openLeftSidebarApp>,
) {
  const { app } = action.payload;

  if (app === MainMenuItems.ShareMap) {
    yield handleShareMapMenuItemOpen();
  }
}

function* handleShareMapMenuItemOpen() {
  yield put(openNewModal('ShareMapSelect', undefined));
}

function* clearActiveMapTemplateOnToolAction() {
  const activeMapTemplate: ReturnType<typeof frontendStateLeftSidebarActiveMapTemplateSelector> = yield select(frontendStateLeftSidebarActiveMapTemplateSelector);

  if (notNullsy(activeMapTemplate)) {
    yield put(clearActiveMapTemplate());
  }
}

function* clearTemplateOnAppChange(action: ReturnType<typeof openLeftSidebarApp>) {
  const activeMapTemplate: ReturnType<typeof frontendStateLeftSidebarActiveMapTemplateSelector> = yield select(frontendStateLeftSidebarActiveMapTemplateSelector);

  if (!notNullsy(activeMapTemplate)) {
    return;
  }

  if (activeMapTemplate === MapTemplates.Styling && action.payload.app === SidebarApp.MapSettings) {
    return;
  }

  yield put(clearActiveMapTemplate());
}
